import { useMemo, useCallback } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import _, { uniqBy } from "lodash"

import { DEFAULT_FILTER_VALUE, Q_VENUE } from "constant"
import dayjs from "config/dayjs"

import { useGeoLocation } from "providers/geolocation"
import { useVenue } from "providers/venue"
import { useVenueController } from "hooks/venue/useVenueController"
import { useRecommendedEvent } from "hooks/sponsored-content/recommended"
import { useGetList } from "venue-react"

export const useEvent = () => {
  const { data: allEvents = [] } = useGetList("event", { populate: true })
  const { venues, isMultiVenueProject } = useVenue()
  const navigate = useNavigate()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)
  const [searchParams] = useSearchParams()
  const { recommendedEvents } = useRecommendedEvent()

  const changeVenueFilter = useCallback(
    (venueId) => {
      if (venueId) searchParams.set(Q_VENUE, venueId)
      navigate(`/events/?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  // Filter events by venue
  const filteredEvents = useMemo(
    () =>
      allEvents?.filter(
        ({ properties }) =>
          venueFilter === DEFAULT_FILTER_VALUE ||
          properties?.venue_id === venueFilter
      ) || [],
    [allEvents, venueFilter]
  )

  // Filter recommended events by venue
  const filteredRecommendedEvents = useMemo(
    () =>
      recommendedEvents?.data?.filter(
        ({ properties }) =>
          venueFilter === DEFAULT_FILTER_VALUE ||
          properties?.venue_id === venueFilter
      ) || [],
    [recommendedEvents?.data, venueFilter]
  )

  // Combine, deduplicate, and sort
  const sanitizedEvents = useMemo(() => {
    const allCombinedEvents = [...filteredRecommendedEvents, ...filteredEvents]

    const uniqueEvents = uniqBy(allCombinedEvents, "id")

    return _.orderBy(
      uniqueEvents,
      [
        (event) => (event?.properties?.isAd ? 0 : 1),
        (event) => event?.properties?.order ?? Infinity,
        (event) => dayjs(event?.properties?.start_date).valueOf(),
      ],
      ["asc", "asc", "asc"]
    )
  }, [filteredRecommendedEvents, filteredEvents])

  return {
    venues,
    venueFilter,
    locationVenueId,
    isMultiVenueProject,
    events: sanitizedEvents,
    changeVenueFilter,
  }
}
